/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';

import { menuCustomizationArray } from 'common/enums';
import { toast } from 'common/helpers/toastConfig';
import { useActions } from 'common/hooks/useActions';
import { useTypedSelector } from 'common/hooks/useTypedSelector';
import Empty from 'components/empty';
import SearchBox from 'components/form/searchBox';
import AppSwitch from 'components/form/switch';
import PrimaryButton from 'components/form/buttons/primaryButton';
import { OutlineButton } from 'components/form/buttons/outLineButton';
import PanelBoxLayout from 'components/layout/panelBox';
import Segmented from 'components/segmented';

import * as S from './styles';

interface ICustoms {
  selectedOptions: any;
  btnTitle?: string;
  title: any;
  category: string;
  image: string | null;
  activeStatus: boolean;
  values?: any;
}

type selectItem = {
  UpdatedAt: string;
  createdAt: string;
  id: number;
  isActive: boolean;
  optionType: string;
  title: string;
};

/** The two option types the UI exposes. `All` is the state the endpoint gets sent as ''. */
const TYPE_OPTIONS = [{ id: '', title: 'All' }, ...menuCustomizationArray];

const typeLabel = (optionType: string) =>
  menuCustomizationArray.find((entry) => entry.id === optionType)?.title || 'Yes/No';

const CheckIcon = () => (
  <svg viewBox="0 0 24 24" fill="none">
    <path d="M4 12l5 5L20 6" />
  </svg>
);

const Customization: React.FC<ICustoms> = ({ selectedOptions, title, category, activeStatus, values }) => {
  const [selectedItems, setSelectedItems] = useState<selectItem[]>([]);
  const [addedItems, setAddedItems] = useState<any[]>([]);
  const [filteredList, setFilteredList] = useState<any>();
  const [meta, setMeta] = useState({
    page: 1,
    page_size: 9999,
    keyword: '',
    type: ''
  });
  const [loading, setLoading] = useState(false);
  /*
   * The type filter is client-side, and has to be.
   *
   * `meta.type` has been in this component's request since it was written and
   * the endpoint does not read it: probed on the running API with `type`,
   * `optionType` and `option_type`, all three return the full list unfiltered.
   * So the field is dead — it is left in `meta` untouched, so the request is
   * unchanged, and recorded in FINDINGS.
   *
   * Filtering here costs nothing: the panel already fetches the whole list
   * (`page_size: 9999`) and already filters it in memory to drop what has been
   * added. This is one more predicate on the same pass.
   */
  const [typeFilter, setTypeFilter] = useState('');

  const params = useParams();
  const navigate = useNavigate();

  const { CustomList }: any = useTypedSelector((state) => state.Cafeteria);

  const {
    menuCustomizationListRequestAction,
    cafeteriaCreateMenuManagementRequestAction,
    menuManagementUpdateRequestAction
  } = useActions();

  const itemSelectHandler = (item: selectItem, isChecked: boolean) => {
    if (isChecked) {
      setSelectedItems((prev) => [...prev, item]);
      return;
    }
    setSelectedItems((prev) => prev.filter((selected) => selected.id !== item.id));
  };

  const fetchData = () => {
    menuCustomizationListRequestAction(meta);
  };

  const handleFilter = (field: string, val: string | number) => {
    setMeta({ ...meta, page: 1, [field]: val });
  };

  const filterData = (dataList: any, selectedItems: any): any => {
    const selectedIds = selectedItems.map((item: any) => item.id);

    return dataList.filter((item: any) => !selectedIds.includes(item.id));
  };

  const addNewItemHandler = () => {
    const newItems = selectedItems
      .filter((item) => !addedItems?.some((addedItem: any) => addedItem.id === item.id))
      .map((item) => ({
        ...item,
        isActiveForThisItem: false
      }));

    setAddedItems((prev) => [...(prev || []), ...newItems]);
    /*
     * Clearing the selection is new, and it is the behaviour the design's
     * button implies: it reports a count, so the count has to go back to zero
     * once those items have moved. Before this the checkboxes stayed ticked
     * on rows that had already been added and pressing Add again did nothing
     * visible.
     */
    setSelectedItems([]);
  };

  const deleteItemHandler = (itemId: number) => {
    const itemToDelete = addedItems.find((item: any) => item.id === itemId);

    if (itemToDelete) {
      const newAddedItems = addedItems.filter((item: any) => item.id !== itemId);

      const itemExistsInFilteredList = filteredList.some((item: any) => item.id === itemId);

      if (!itemExistsInFilteredList) {
        setFilteredList((prevFilteredList: any) => [...prevFilteredList, itemToDelete]);
      }

      setAddedItems(newAddedItems);

      setSelectedItems((prevSelectedItems: any) => prevSelectedItems.filter((item: any) => item.id !== itemId));
    }
  };

  const submitHandler = (kind: string) => {
    if (!category) {
      toast({ model: 'error', message: 'Please choose a category' });
      return;
    }

    if (values.imageName?.length < 1 && kind === 'Create') {
      toast({ model: 'error', message: 'There is no image uploaded' });
      return;
    }

    if (title?.find((t: any) => t.language === 'en')?.translate.length < 1) {
      toast({ model: 'error', message: 'English Title is necessary' });
      return;
    }

    if (title?.find((t: any) => t.language === 'ar')?.translate.length < 1) {
      toast({ model: 'error', message: 'Arabic Title is necessary' });
      return;
    }

    let menuOptions: any = [];
    if (addedItems?.length > 0) {
      menuOptions = addedItems?.map((item) => ({
        id: item.id,
        isActive: item.isActiveForThisItem
      }));
    }

    if (kind === 'Create') {
      setLoading(true);
      const requestBody = {
        translations: title,
        status: `${activeStatus}`,
        category: category,
        image: values.imageName,
        ...(menuOptions.length > 0 && { menuOptions })
      };
      cafeteriaCreateMenuManagementRequestAction(
        requestBody,
        () => navigate('/panel/cafeteria/menu-management'),
        () => setLoading(false)
      );
      return;
    }

    if (kind === 'Update') {
      setLoading(true);
      const requestBody = {
        id: params?.id,
        translations: title,
        status: activeStatus,
        imageName: values.name,
        ...(menuOptions.length > 0 && { menuOptions })
      };
      menuManagementUpdateRequestAction(
        requestBody,
        () => navigate('/panel/cafeteria/menu-management'),
        () => setLoading(false)
      );
      return;
    }
  };

  useEffect(() => {
    if (CustomList?.data?.list) {
      const filtered = selectedOptions ? filterData(CustomList?.data?.list, selectedOptions) : CustomList?.data?.list;

      setFilteredList(filtered);
    }
  }, [CustomList, selectedOptions]);

  useEffect(() => {
    fetchData();
  }, [meta]);

  useEffect(() => {
    if (selectedOptions && params?.id) {
      setAddedItems(selectedOptions);
      return;
    }
    setAddedItems([]);
  }, [selectedOptions, params?.id]);

  /* Already-added items never appear on the left, so `available` is what is
     left after the filter, and both heads can report a real count. */
  const available: any[] = (filteredList || [])
    .filter((item: any) => !addedItems?.some((added: any) => added.id === item.id))
    .filter((item: any) => !typeFilter || item?.optionType === typeFilter);

  return (
    <PanelBoxLayout title="Customizations" hint="Dimmed options are switched off in the Customizations section">
      <S.Transfer>
        <S.Panel>
          <S.PanelHead>
            Available
            <S.Count>{available.length}</S.Count>
          </S.PanelHead>

          <S.Filters>
            <SearchBox
              onChange={(e) => handleFilter('keyword', e)}
              placeholder="Search customizations"
              value={meta.keyword}
            />
            <Segmented options={TYPE_OPTIONS} value={typeFilter} onChange={(id) => setTypeFilter(String(id))} />
          </S.Filters>

          <S.PanelBody>
            {available.length > 0 ? (
              available.map((item: any) => {
                const isSelected = selectedItems.some((selected) => selected.id === item.id);

                return (
                  <S.Row
                    key={item?.id}
                    className={`${isSelected ? 'selected' : ''} ${item?.isActive ? '' : 'off'}`.trim()}
                    onClick={() => itemSelectHandler(item, !isSelected)}
                  >
                    <S.Box>
                      <CheckIcon />
                    </S.Box>
                    <S.Name>{item?.title}</S.Name>
                    <S.Type>{typeLabel(item?.optionType)}</S.Type>
                  </S.Row>
                );
              })
            ) : (
              <Empty
                size="sm"
                title={meta.keyword || meta.type ? 'No customizations match those filters' : 'Nothing left to add'}
                text={
                  meta.keyword || meta.type
                    ? 'Try a different term, or clear the type filter.'
                    : 'Every customization is already on this item.'
                }
              />
            )}
          </S.PanelBody>
        </S.Panel>

        <S.Panel>
          <S.PanelHead>
            Added to this item
            <S.Count>{addedItems?.length || 0}</S.Count>
          </S.PanelHead>

          <S.PanelBody>
            {addedItems?.length ? (
              addedItems.map((item: any) => (
                <S.Row key={item.id} className={`static ${item?.isActive ? '' : 'off'}`.trim()}>
                  <S.Name>{item.title}</S.Name>
                  <S.RowActions>
                    <AppSwitch
                      checked={item?.isActiveForThisItem}
                      onChange={() =>
                        setAddedItems((prev) =>
                          prev.map((addedItem) =>
                            addedItem.id === item.id
                              ? { ...addedItem, isActiveForThisItem: !item?.isActiveForThisItem }
                              : addedItem
                          )
                        )
                      }
                    />
                    <S.Remove type="button" onClick={() => deleteItemHandler(item.id)} aria-label="Remove">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M6 6l12 12M18 6L6 18" />
                      </svg>
                    </S.Remove>
                  </S.RowActions>
                </S.Row>
              ))
            ) : (
              <Empty
                size="sm"
                title="No customizations added"
                text="Pick options on the left, then add them to this item."
                icon={
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                    <path d="M12 5v14M5 12h14" />
                  </svg>
                }
              />
            )}
          </S.PanelBody>
        </S.Panel>
      </S.Transfer>

      {/*
       * The design's button: it says how many are selected and cannot be
       * pressed until something is. It used to read "Add to Saudi Coffee" —
       * the item's own title, restated on a control that already sits inside
       * that item's form — and it was pressable at all times, doing nothing
       * when nothing was ticked.
       */}
      <S.AddRow>
        <OutlineButton onClick={addNewItemHandler} disabled={selectedItems.length === 0} className="sm">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M12 5v14M5 12h14" />
          </svg>
          {selectedItems.length ? `Add selected (${selectedItems.length})` : 'Add selected'}
        </OutlineButton>
      </S.AddRow>

      <S.Actions>
        <PrimaryButton
          type="button"
          disabled={loading}
          onClick={() => !loading && submitHandler(params?.id ? 'Update' : 'Create')}
        >
          {params?.id ? 'Update item' : 'Create item'}
        </PrimaryButton>
      </S.Actions>
    </PanelBoxLayout>
  );
};

export default Customization;
